import React from "react";
import AvailabilitySearchCalendar from "@bwp/AvailabilitySearchCalendar";
import {DateAvailabilitiesQuery, useDateAvailabilitiesQuery} from "@bwp/operations.generated";
import {SearchContext, serializeDate} from "@bwp/shared/searchcontext";
import useGlobalSearchContext from "@bwp/shared/useGlobalSearchContext";
import {AvailabilitySearchCalendarTranslations} from "@bwp/translations/AvailabilitySearchCalendarTranslations";

export type DateAvailability = DateAvailabilitiesQuery["dateAvailabilities"][0];

const DefaultSearchContext = {
    adults: 2,
    children: 0,
    infants: 0,
    durations: [2,3,4,5,6,7]
};

export default function AvailabilitySearchCalendarWrapper({
    durations,
    adults,
    children,
    infants,
    translations
} : {
    durations: number[],
    adults: number,
    children: number,
    infants: number,
    translations: AvailabilitySearchCalendarTranslations
}) {
    const [searchContext, setSearchContext] = useGlobalSearchContext({});
    const [month, setMonth] = React.useState(new Date().getMonth() + 1);
    const [year, setYear] = React.useState(new Date().getFullYear());
    const [currentDate, setCurrentDate] = React.useState(null);
    
    const searchContextProps = {
        adults: adults ?? DefaultSearchContext.adults,
        children: children ?? DefaultSearchContext.children,
        infants: infants ?? DefaultSearchContext.infants,
        durations: durations ?? DefaultSearchContext.durations
    };
    
    const { data, loading, error } = useDateAvailabilitiesQuery({
        variables: {
            month,
            year,
            query: SearchContext.createFromSearchContextProps(searchContextProps).toServerQuery()
        }
    });
    
    const handleMonthChanged = (month: number, year: number) => {
        setMonth(month);
        setYear(year);
        if (currentDate && (currentDate.getMonth()+1 === month && currentDate.getFullYear() === year)) {
            setSearchContext(SearchContext.createFromSearchContextProps({
                ...searchContextProps,
                arrival: serializeDate(currentDate)
            }));        
        } else {
            setSearchContext(SearchContext.createFromSearchContextProps({}));
        }
    }
    
    const handleCurrentDateChanged = (date: Date) => {
        setCurrentDate(date);
        const searchContext = SearchContext.createFromSearchContextProps({
            ...searchContextProps,
            arrival: serializeDate(date)
        });
        setSearchContext(searchContext);
    }
    
    const daysWithAvailability = (loading || error)
        ? []
        : data.dateAvailabilities.map(da => ({
            date: new Date(da.date),
            availability: da.availability
        }));
    
    return (
        <AvailabilitySearchCalendar
            durations={durations}
            adults={adults}
            children={children}
            infants={infants}
            translations={translations}
            loading={loading}
            month={month}
            year={year}
            onMonthChanged={handleMonthChanged}
            currentDate={currentDate}
            onCurrentDateChanged={handleCurrentDateChanged}
            daysWithAvailability={daysWithAvailability}
        />
    );
}